import { Head } from "@inertiajs/react"
import { GraduationCap, Users, Sparkles } from "lucide-react"

import GoogleIcon from "@/components/icons/google-icon"
import { Button } from "@/components/ui/button"
import MarketingLayout from "@/layouts/marketing-layout"
import { getMetaContent } from "@/utils/meta"

export default function TeachThai() {
  return (
    <MarketingLayout>
      <Head title="Teach Thai on ThaiCopilot">
        <meta
          name="description"
          content="Join ThaiCopilot as a verified Thai teacher. Reach expats and travelers who want to learn Thai. Build your teaching profile and connect with motivated learners."
        />
      </Head>

      <section className="relative flex min-h-[85vh] items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-indigo-50 via-white to-violet-50 dark:from-slate-900 dark:via-slate-950 dark:to-indigo-950/30" />
        <div
          className="absolute inset-0 -z-10 opacity-[0.03]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgb(99 102 241) 1px, transparent 0)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="container mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="mb-8 flex justify-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-100/80 px-4 py-2 text-sm font-medium text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300">
                <GraduationCap className="size-4" />
                For Thai Teachers
              </div>
            </div>

            {/* Headline */}
            <h1 className="mb-6 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl md:text-6xl dark:text-white">
              Share Your Thai.{" "}
              <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent dark:from-indigo-400 dark:to-violet-400">
                Reach Learners Worldwide.
              </span>
            </h1>

            {/* Subtext */}
            <p className="mx-auto mb-12 max-w-2xl text-lg leading-relaxed text-slate-600 sm:text-xl dark:text-slate-300">
              Join ThaiCopilot as a verified teacher. Build a professional
              profile, connect with motivated expats and travelers, and grow
              your teaching practice.
            </p>

            {/* Value props */}
            <div className="mx-auto mb-12 flex max-w-lg flex-col gap-4 sm:flex-row sm:justify-center">
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <Users className="size-4 text-indigo-500" />
                <span>Motivated learners</span>
              </div>
              <div className="hidden sm:block text-slate-300 dark:text-slate-700">|</div>
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <GraduationCap className="size-4 text-indigo-500" />
                <span>Verified profile</span>
              </div>
              <div className="hidden sm:block text-slate-300 dark:text-slate-700">|</div>
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <Sparkles className="size-4 text-indigo-500" />
                <span>Free to join</span>
              </div>
            </div>

            {/* Google Sign Up CTA */}
            <form action="/auth/google_oauth2" method="post">
              <input
                type="hidden"
                name="authenticity_token"
                value={getMetaContent("csrf-token") || ""}
              />
              <input
                type="hidden"
                name="origin"
                value="/teach-thai?role=teacher"
              />
              <Button
                type="submit"
                size="lg"
                className="gap-2 px-8 text-base shadow-lg shadow-indigo-500/20 transition-transform hover:scale-105"
              >
                <GoogleIcon className="size-5" />
                Sign up with Google
              </Button>
            </form>

            <p className="mt-4 text-sm text-slate-400 dark:text-slate-500">
              Already have an account? Sign in with Google above.
            </p>
          </div>
        </div>

        {/* Bottom gradient */}
        <div className="absolute right-0 bottom-0 left-0 h-32 bg-gradient-to-b from-transparent to-white dark:to-slate-950" />
      </section>
    </MarketingLayout>
  )
}
